


































































import { Vue, Component, Ref } from "vue-property-decorator";
import { ArticleClass, BillingCatalog, BillingItem } from "@/data/models/BillingCatalog";
import {
  fetchArticleClasses,
  fetchBillingCatalog,
  fetchBillingItems,
  updateBillingItem,
} from "@/data/repository/BillingCatalogRepository";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";
import { ViewerSection, ViewerColumn, CellEditEvent } from "@/components/common/data-viewer/DataViewerModels";
import DataViewerComponent from "@/components/common/data-viewer/DataViewer.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { editDataViewerAccess } from "@/permissions/UserPermissions";
import { getActiveUser } from "@/data/repository/UserRepository";
import router from "@/router/router";
import { setPendingChangesGuard, clearPendingChangesGuard } from "@/utils/PendingChangesGuard";
import {
  downloadDeviceExport,
  ExportFormat,
  getCsvExportFormatLabel,
  getExportFormatIcon,
} from "@/utils/DeviceExportFormats";
import { CSV_SEPARATOR } from "@/utils/Utils";


/**
 * Detail view for a single billing catalog (Leistungsverzeichnis).
 * Fetches billing items and article classes, then groups items into
 * collapsible sections per article class using the DataViewer component.
 */

@Component({
  components: {
    "data-viewer": DataViewerComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
  },
})
export default class BillingCatalogDetailComponent extends Vue {
  @Ref("dataViewer") dataViewerComponent!: DataViewerComponent;
  @Ref("confirm") confirmationDialogComponent!: ConfirmationDialogComponent;

  isLoading = true;
  loadError = false;
  saving = false;
  sections: ViewerSection[] = [];
  catalog: BillingCatalog | null = null;

  /** Map from "sectionId:rowIndex" to BillingItem Firestore doc ID */
  private itemIdMap = new Map<string, string>();

  selectedFormat: ExportFormat = ExportFormat.CSV;

  /** The three export formats, each with the icon that names that file type app-wide. */
  get formatOptions() {
    return [
      {
        label: getCsvExportFormatLabel(),
        value: ExportFormat.CSV,
        icon: getExportFormatIcon(ExportFormat.CSV),
      },
      {
        label: "Excel (.xlsx)",
        value: ExportFormat.EXCEL,
        icon: getExportFormatIcon(ExportFormat.EXCEL),
      },
      { label: "JSON", value: ExportFormat.JSON, icon: getExportFormatIcon(ExportFormat.JSON) },
    ];
  }

  get catalogId(): string {
    return this.$route.params.catalogId;
  }

  get viewerTitle(): string {
    return this.$t("settings.billingCatalogs.detail.title").toString();
  }

  get canEdit(): boolean {
    return editDataViewerAccess(getActiveUser());
  }

  /** Read-only validity period text for the header bar. */
  get validityText(): string {
    if (!this.catalog) return "";
    const from = this.catalog.validFrom;
    const until = this.catalog.validUntil;
    if (!from && !until) return "";
    const fromStr = from
      ? from.toDate().toLocaleDateString()
      : "–";
    const untilStr = until
      ? until.toDate().toLocaleDateString()
      : this.$t("settings.billingCatalogs.detail.validityOpenEnded").toString();
    const fromLabel = this.$t("settings.billingCatalogs.columns.validFrom").toString();
    const untilLabel = this.$t("settings.billingCatalogs.columns.validUntil").toString();
    return `${fromLabel}: ${fromStr}  ·  ${untilLabel}: ${untilStr}`;
  }

  async mounted() {
    this.setToolbarTitle();
    setPendingChangesGuard(() => this.confirmDiscard());
    try {
      const [catalog, items, classes] = await Promise.all([
        fetchBillingCatalog(this.catalogId),
        fetchBillingItems(this.catalogId),
        fetchArticleClasses(this.catalogId),
      ]);
      this.catalog = catalog;
      this.sections = this.buildSections(items, classes);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to load billing catalog detail:", error);
      this.loadError = true;
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Groups billing items by article class into ViewerSections.
   * Each article class becomes a collapsible section.
   * Items without a matching class go into an "Uncategorized" section.
   */
  buildSections(items: BillingItem[], classes: ArticleClass[]): ViewerSection[] {
    const columns = this.getColumns();

    // Build a map of classId -> ArticleClass for lookup
    const classMap = new Map<string, ArticleClass>();
    for (const cls of classes) {
      classMap.set(cls.classId, cls);
    }

    // Group items by articleClass
    const grouped = new Map<string, BillingItem[]>();
    for (const item of items) {
      const key = item.articleClass || "";
      const group = grouped.get(key);
      if (group) {
        group.push(item);
      } else {
        grouped.set(key, [item]);
      }
    }

    const sections: ViewerSection[] = [];

    // Sort by class id ascending: the ids are numbered by design, so that is the
    // LV's own section order. Items with no class ("") stay in a trailing section.
    const sortedClassIds = Array.from(grouped.keys()).sort((a, b) => {
      if (a === "") return 1;
      if (b === "") return -1;
      return a.localeCompare(b);
    });

    for (const classId of sortedClassIds) {
      const classInfo = classMap.get(classId);
      const classItems = grouped.get(classId) || [];

      // Sort items by articleId
      classItems.sort((a, b) => a.articleId.localeCompare(b.articleId));

      const sectionId = classId || "uncategorized";
      // Four QUK classes have no description in X360; show the id rather than a
      // blank heading, without the import inventing a description.
      const sectionName = classInfo
        ? classInfo.description || classId
        : this.$t("settings.billingCatalogs.detail.uncategorized").toString();

      // Store item ID mapping for save operations
      classItems.forEach((item, index) => {
        this.itemIdMap.set(`${sectionId}:${index}`, item.id);
      });

      sections.push({
        id: sectionId,
        name: sectionName,
        type: classId ? classId : "",
        columns,
        rows: classItems.map((item) => ({
          articleId: item.articleId,
          headline: item.headline,
          text: item.text,
          unit: item.unit,
          price: item.price,
          limit: item.limit,
          qualifier: item.qualifier,
        })),
        expandedByDefault: false,
      });
    }

    // Auto-expand if there is only one section
    if (sections.length === 1) {
      sections[0].expandedByDefault = true;
    }

    return sections;
  }

  /** Column definitions for billing item tables. */
  getColumns(): ViewerColumn[] {
    const t = (key: string) =>
      this.$t(`settings.billingCatalogs.detail.columns.${key}`).toString();

    return [
      { key: "articleId", label: t("articleId"), width: "120px", searchable: true },
      { key: "headline", label: t("headline"), width: "25%", searchable: true },
      { key: "text", label: t("text"), width: "35%", searchable: true },
      { key: "unit", label: t("unit"), width: "80px" },
      { key: "price", label: t("price"), width: "100px" },
      { key: "limit", label: t("limit"), width: "100px", editable: true, editType: "number" },
      { key: "qualifier", label: t("qualifier"), width: "120px", editable: true, editType: "text" },
    ];
  }

  /**
   * Persists pending edits to Firestore.
   * Resolves each edit's sectionId + rowIndex to the original BillingItem doc ID.
   */
  async onSave(edits: CellEditEvent[]) {
    this.saving = true;
    try {
      const updates = new Map<string, Partial<Pick<BillingItem, "limit" | "qualifier">>>();

      for (const edit of edits) {
        const itemId = this.itemIdMap.get(`${edit.sectionId}:${edit.rowIndex}`);
        if (!itemId) continue;

        const existing = updates.get(itemId) || {};
        if (edit.columnKey === "limit") {
          existing.limit = edit.newValue != null && edit.newValue !== ""
            ? Number(edit.newValue)
            : null;
        } else if (edit.columnKey === "qualifier") {
          existing.qualifier = edit.newValue != null && edit.newValue !== ""
            ? String(edit.newValue)
            : null;
        }
        updates.set(itemId, existing);
      }

      const promises = Array.from(updates.entries()).map(([itemId, fields]) =>
        updateBillingItem(this.catalogId, itemId, fields)
      );
      await Promise.all(promises);

      this.dataViewerComponent.clearPendingEdits();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to save billing item edits:", error);
    } finally {
      this.saving = false;
    }
  }

  async onBack() {
    router.back();
  }

  /** Export billing items in the selected format. */
  async onDownload() {
    if (this.sections.length === 0) return;
    const columns = this.getColumns();
    const headers = columns.map((c) => c.label);
    const dataRows: string[] = [];
    for (const section of this.sections) {
      for (const row of section.rows) {
        const values = columns.map((c) => {
          const val = row[c.key];
          return val != null ? String(val) : "";
        });
        dataRows.push(values.join(CSV_SEPARATOR));
      }
    }
    await downloadDeviceExport(headers, dataRows, this.catalogId, this.selectedFormat);
  }

  beforeDestroy() {
    clearPendingChangesGuard();
  }

  /**
   * Checks for unsaved changes and shows a discard confirmation dialog.
   * Returns `true` to allow navigation, `false` to block.
   */
  async confirmDiscard(): Promise<boolean> {
    if (!this.dataViewerComponent?.hasChanges) return true;
    return this.confirmationDialogComponent.show({
      title: this.$t("common.confirmationDialog.cancelTitle").toString(),
      message: this.$t("common.confirmationDialog.cancelMessage").toString(),
    });
  }

  setToolbarTitle() {
    const baseTitle = this.$t("pages.settings").toString();
    const sectionTitle = this.$t("settings.billingCatalogs.title").toString();
    const detailTitle = this.$t("settings.billingCatalogs.detail.title").toString();
    ToolbarContentModule.setToolbarTitle(
      baseTitle + " > " + sectionTitle + " > " + detailTitle
    );
  }
}
